import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {useSelector} from 'react-redux';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import './index.scss';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {GatewayModel} from '../../../../types/gateway';
import {useMapSelector} from '../../../../hooks/map-selector';
import {BridgeDeviceTypeModel} from '../../../../types/bridge-device-type';
import {useMessage} from '../../../../hooks/message';
import {isValidRFAddress} from '../../../../utils/validation';
import {isValidIPAddress} from '../../../../utils/validation';
import {useRPC} from '../../../../hooks/rpc';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';
import {useLocalization} from "../../../../hooks/localization";
import {DeviceTypes} from "../../../../constants/DeviceTypes";
const BACK_URL = '/settings/device';

export const DeviceEditScreen = memo(function DeviceEditScreen() {
    const [inputStatesDeviceTypes, setInputStatesDeviceTypes] = useState(false);
    const {t} = useLocalization()
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard] = useKeyboard('numpad', true);
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);



    const alarmConfig = useSelector(state => state.control.config.alarm);
    const GATEWAY_OPTION_ITEM_LIST = useMapSelector(
        state => state.control.gatewayList,
        gatewayModel => ({value: gatewayModel, label: gatewayModel.name}),
        [],
    );
    const DEVICE_TYPE_SELECTION_ITEMS = useMapSelector(
        state => state.control.bridgeDeviceTypeList,
        device => ({value: device, label: device.name}),
        [],
    );

    const [selected, setSelected] = useState<BridgeDeviceModel>(null);
    const [name, setName] = useState('');
    const [type, setType] = useState<SelectItem<BridgeDeviceTypeModel>>(null);
    const [rfAddress, setRFAddress] = useState('');
    const [ipAddress, setIPAddress] = useState('');
    const [gateway, setGateway] = useState<SelectItem<GatewayModel>>(null);

    const nextFreeRfId = useMemo(() => {
        const usedRfAddressList = bridgeDeviceList.filter(v => v.gateway_id === gateway?.value?.id).map(value => value.rf_address);
        for (let i = 10; i < 255; i++) {
            if (!usedRfAddressList.includes(i)) {
                return i.toString();
            }
        }
        return '';
    }, [bridgeDeviceList, gateway?.value?.id]);


    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteBridgeDevice(selected.id)
            .then(() => {
                success(t('device_successfully_deleted'));
                onBackPressed();
            })
            .catch(failure);
    }, [t, failure, onBackPressed, rpc, selected, success]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_a_valid_device_name'));

            return false;
        }

        if (type == null || type.value == null) {
            failure(t('pls_select_a_valid_device_type'));

            return false;
        }

        if (!isValidRFAddress(rfAddress)) {
            failure(t('pls_enter_a_valid_rf_address'));

            return false;
        }
        if (type.value.id === DeviceTypes.ETHERNET_IO){
            if (!isValidIPAddress(ipAddress)) {
                failure(t('pls_enter_a_valid_ip_address'));
                return false;
            }
        }

        if (gateway == null || gateway.value == null) {
            failure(t('pls_select_a_valid_gateway'));

            return false;
        }

        return true;
    }, [t, failure, gateway, name, rfAddress, type, ipAddress]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const bridgeDeviceModel: BridgeDeviceModel = {
            ...selected,
            id: selected != null ? selected.id : null,
            type_id: type.value.id,
            name,
            rf_address: Number(rfAddress),
            unique_device_id: ipAddress,
            gateway_id: gateway.value.id,
            temperature_offset: selected != null ? selected.temperature_offset : 0,
            temperature_hysteresis: selected != null ? selected.temperature_hysteresis : 2,
            alarm_config: selected != null ? selected.alarm_config : alarmConfig,
        };

        return rpc.updateBridgeDevice(bridgeDeviceModel)
            .then(() => {
                success(t(bridgeDeviceModel.id ? 'device_successfully_edited' : 'device_successfully_created'));
                onBackPressed();
            })
            .catch(failure);
    }, [
        t,
        isFieldsValid,
        name,
        type,
        rfAddress,
        ipAddress,
        gateway,
        alarmConfig,
        selected,
        rpc,
        success,
        failure,
        onBackPressed,
    ]);

    const buttons = useMemo(() => [
        {
            component: selected == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type="bridgeDevice"
                    onPress={onDeletePressed}
                    questionText={t("are_you_sure_you_want_to_delete")}
                    questionTextRelational={message => t('this_will_be_deleted_with_this_message__continue',{name:selected?.name, message})}/>,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, onCreatePressed, onDeletePressed, selected]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    useEffect(() => {
        if (params.id != null) {
            return;
        }
        setType(DEVICE_TYPE_SELECTION_ITEMS[0]);
        setGateway(GATEWAY_OPTION_ITEM_LIST[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [GATEWAY_OPTION_ITEM_LIST]);

    useEffect(() => {
        console.log({type})
        if (type == null) {
            return;
        }
        console.log(type.value.id)
        if (type.value.id ===  DeviceTypes.ETHERNET_IO){
            setInputStatesDeviceTypes(true);
        }else {
            setInputStatesDeviceTypes(false);
        }
    }, [type])

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const device = bridgeDeviceList.find(device => device.id === params['id']);
        if (device == null) {
            return;
        }

        setSelected(device);
        setName(device.name);
        setType(DEVICE_TYPE_SELECTION_ITEMS.find(option => option.value.id === device.type_id));
        setGateway(GATEWAY_OPTION_ITEM_LIST.find(gatewayOptionItem => gatewayOptionItem.value.id === device.gateway_id));
        setRFAddress(device.rf_address.toString());
        //setIPAddress(device.ip_address.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id == null) {
            setRFAddress(nextFreeRfId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextFreeRfId]);

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer className="service-edit" buttons={buttons} backUrl={BACK_URL}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t("write_device_name")}
                            label={t("device_name")}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t("device_type")}
                            options={DEVICE_TYPE_SELECTION_ITEMS}
                            onChange={setType}
                            value={type}/>
                        {
                            inputStatesDeviceTypes
                            ?
                            <SettingInputComponent
                                placeholder={t("pls_enter_a_valid_ip_address")}
                                label={t("ip_address")}
                                value={ipAddress}
                                setValue={setIPAddress}
                                setActiveInput={setActiveNumpadInput}
                                onBlur={onBlur}/>
                            :
                            <SettingInputComponent
                                placeholder={t("write_rf_address")}
                                label={t("rf_address")}
                                value={rfAddress}
                                setValue={setRFAddress}
                                setActiveInput={setActiveNumpadInput}
                                onBlur={onBlur}/>
                        }
                        <SettingSelectComponent
                            label={t("gateway_name")}
                            options={GATEWAY_OPTION_ITEM_LIST}
                            onChange={setGateway}
                            value={gateway}/>
                    </div>
                    <div className="content-wrapper">

                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
